import { X } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { SchedulingScreen } from '@/pages/SchedulingScreen';

interface SchedulingSidePanelProps {
  readonly isOpen: boolean;
  readonly onClose: () => void;
  readonly onDialogOpenChange?: (isOpen: boolean) => void;
  readonly onNavigateToCalendar?: () => void;
  readonly sidebarExpanded?: boolean;
}

export function SchedulingSidePanel({ 
  isOpen, 
  onClose,
  onDialogOpenChange,
  onNavigateToCalendar,
  sidebarExpanded = false,
}: SchedulingSidePanelProps) {
  const handleNavigateToCalendar = () => {
    onClose();
    onNavigateToCalendar?.();
  };
  
  // Calculate left offset based on sidebar state
  // Collapsed: 56px (left-14), Expanded: ~240px
  const leftOffset = sidebarExpanded ? 'left-60' : 'left-14';

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop - only covers content area, not sidebar */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className={`fixed ${leftOffset} top-0 right-0 bottom-0 bg-black/60 z-40 transition-[left] duration-300`}
            onClick={onClose}
          />
          
          {/* Side panel - positioned after sidebar, moves with sidebar expansion */}
          <motion.div
            initial={{ x: '-100%', opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            exit={{ x: '-100%', opacity: 0 }}
            transition={{ type: 'spring', damping: 30, stiffness: 300 }}
            className={`fixed ${leftOffset} top-0 h-screen w-96 bg-[#0E0E0E] z-50 overflow-hidden flex flex-col transition-[left] duration-300`}
          >
            {/* Separator line on left edge */}
            <div className="absolute left-0 top-0 bottom-0 w-px bg-white/[0.08]" />
            
            {/* Header with close button */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-white/[0.04] shrink-0">
              <h2 className="text-lg font-medium text-white">Réserver un tournage</h2>
              <button
                onClick={onClose}
                className="p-2 -mr-2 rounded-full hover:bg-white/5 transition-colors"
              >
                <X className="h-5 w-5 text-white/60" />
              </button>
            </div>
            
            {/* Content - scrollable */}
            <div className="flex-1 overflow-y-auto">
              <SchedulingScreen 
                onDialogOpenChange={onDialogOpenChange}
                onNavigateToCalendar={handleNavigateToCalendar}
                hideHeader
              />
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
